import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  output,
  Output,
  signal,
  ViewChild
} from "@angular/core";
import { NavigationBarItem } from "./navigation-bar-item";
import { NavigationEnd, Router } from "@angular/router";
import { BoxService } from "src/app/services/box.service";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import {
  UpdateService,
  UpdateServiceStatus
} from "src/app/services/update.service";
import { sntNavigate } from "src/app/support/router";
import {
  getCurrentSoftwareProductVersion,
  SoftwareProduct
} from "src/app/support/product";
import {
  ScannerService,
  ScannerStatus
} from "src/app/services/scanner.service";

import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";
import { environment } from "src/environments/environment";
import { COLORS } from "src/app/support/colors";
import { dateUtil } from "src/app/support/date-util";
import { PRODUCT_TOKEN } from "src/app/core/product.token";
import { BrokerService, BrokerStatus } from "src/app/sse/broker.service";
import { ProtectedTabService } from "src/app/services/protected-tab.service";

const packageJson = require("../../../../../../package.json");

export enum NavigationBarMode {
  FLOATING = "floating",
  BLOCK = "block",
  INLINE = "inline"
}

@Component({
  standalone: false,
  selector: "snoots-bootstrap-navigation-bar",
  templateUrl: "navigation-bar.component.html",
  styles: `
    :host {
      height: 100%;
      display: flex;
      flex-direction: row;
    }
  `
})
export class NavigationBarComponent {
  protected readonly product = inject(PRODUCT_TOKEN);
  protected readonly SoftwareProduct = SoftwareProduct;

  @ViewChild("secondaryMenu") secondaryMenu: ElementRef<HTMLDivElement> | null =
    null;

  @ViewChild("iconSSEStatus")
  iconSSEStatus: ElementRef<HTMLElement> | undefined;

  @Output()
  primaryMenuItem: EventEmitter<NavigationBarItem> =
    new EventEmitter<NavigationBarItem>();

  productTitle: string = "";
  navigationMenu: NavigationBarItem[] = [];

  activePrimaryMenuItem: NavigationBarItem | null = null;

  productName: string = "";
  version: string = "";

  buildVersion: string = packageJson.version;
  productVersion: string = "";
  buildNumber: string | undefined = packageJson.build;
  branch: string | undefined = packageJson.branch;
  commitHash: string | undefined = packageJson.commit;

  isStaging = !environment.snoots.local && !environment.snoots.production;

  protected readonly COLORS = COLORS;

  currentYear: string = dateUtil().format("YYYY");

  mode = input.required<NavigationBarMode>();
  toggleMode = output<void>();

  protected readonly scannerStatus = signal<ScannerStatus>("disabled");
  protected readonly serverEventsStatus = signal<BrokerStatus>("connecting");
  protected readonly updateStatus = signal<UpdateServiceStatus>(
    UpdateServiceStatus.DISABLED
  );

  uiInfo = computed<{ name: string; logo: string }>(() => {
    const uiRenderInfo: Record<string, { name: string; logo: string }> = {
      [SoftwareProduct.ROCKET]: {
        name: "Rocket",
        logo: `/assets/favicons/rocket-${this.currentTheme}.png`
      },
      [SoftwareProduct.PMS]: {
        name: "Snoots PMS",
        logo: "/assets/images/SNOOTS_color.png"
      },
      [SoftwareProduct.PLATFORM]: {
        name: "Referral Platform",
        logo: "/assets/images/SNOOTS_color.png"
      },
      [SoftwareProduct.DROP_PAGE]: {
        name: "Drop Page",
        logo: "/assets/images/SNOOTS_color.png"
      },
      [SoftwareProduct.BOTMASTER]: {
        name: "Botmaster",
        logo: "/assets/images/botmaster-logo.png"
      }
    };

    const currentProduct = this.bootstrap.getCurrentProduct();

    return uiRenderInfo[currentProduct] ?? { name: "", logo: "" };
  });

  protected readonly currentTheme = inject(BootstrapService).getCurrentTheme();

  private timeoutRef: number | undefined;

  currentOpenedMenu:
    | {
        container: HTMLElement;
        wrapper: HTMLElement;
        index: number;
      }
    | undefined;

  protected readonly PLATFORM = inject(PLATFORM_TOKEN);
  protected readonly PlatformType = PlatformType;
  protected readonly UpdateServiceStatus = UpdateServiceStatus;
  protected readonly NavigationBarMode = NavigationBarMode;

  private readonly router = inject(Router);
  private readonly bootstrap = inject(BootstrapService);
  private readonly scannerService = inject(ScannerService);
  private readonly updateService = inject(UpdateService);
  readonly #brokerService = inject(BrokerService);
  readonly #protectedTabService = inject(ProtectedTabService);
  readonly protectedRoutesRevision =
    this.#protectedTabService.protectedRoutesRevision;

  constructor() {
    this.productVersion = getCurrentSoftwareProductVersion();

    this.#brokerService.message$.subscribe(() => {
      if (this.timeoutRef) {
        clearTimeout(this.timeoutRef);
        this.timeoutRef = undefined;
      } else {
        this.iconSSEStatus?.nativeElement?.classList.remove("fa-signal-stream");
        this.iconSSEStatus?.nativeElement?.classList.add("fa-circle-down");
      }

      this.timeoutRef = <any>setTimeout(() => {
        this.timeoutRef = undefined;
        this.iconSSEStatus?.nativeElement?.classList.remove("fa-circle-down");
        this.iconSSEStatus?.nativeElement?.classList.add("fa-signal-stream");
      }, 1000);
    });

    this.updateService.updating$.subscribe((status: UpdateServiceStatus) => {
      this.updateStatus.set(status);
    });

    this.#brokerService.status$.subscribe((status: BrokerStatus) => {
      this.serverEventsStatus.set(status);
    });

    this.bootstrap.productTitle$.subscribe((title) =>
      setTimeout(() => {
        this.productTitle = title;
      })
    );

    this.bootstrap.menu$.subscribe((menu) => {
      setTimeout(() => {
        this.navigationMenu = menu;

        this.bootstrap.updateActiveStatusByUrl(menu, location.pathname);
      });
    });

    switch (this.bootstrap.getCurrentProduct()) {
      case "PMS": {
        this.productName = packageJson.products.pms.displayName;
        this.version = packageJson.products.pms.versionName;
        break;
      }
      case "Rocket": {
        this.productName = packageJson.products.rocket.displayName;
        this.version = packageJson.products.rocket.versionName;
        break;
      }
    }

    this.router.events.subscribe((routerEvent) => {
      if (routerEvent instanceof NavigationEnd) {
        this.bootstrap.updateActiveStatusByUrl(
          this.navigationMenu,
          routerEvent.url
        );
      }
    });

    this.scannerService.status.subscribe((status: ScannerStatus) =>
      this.scannerStatus.set(status)
    );
  }

  isActive(navigationMenuItem: NavigationBarItem): boolean {
    if (!navigationMenuItem.url) {
      return false;
    }

    return location.pathname.startsWith(navigationMenuItem.url);
  }

  getActiveNavigationMenu(): NavigationBarItem | null {
    return this.activePrimaryMenuItem;
  }

  onUpdateStatusClick(): void {
    this.updateService.applyUpdate("navigation_bar");
  }

  onMenuItemClick(item: NavigationBarItem): void {
    if (!item.url) {
      return;
    }

    if (this.currentOpenedMenu) {
      let { container, wrapper } = this.currentOpenedMenu;

      this.#closeSecondaryMenu(container, wrapper);
      this.currentOpenedMenu = undefined;
    }

    sntNavigate(this.router, [item.url], {
      queryParams: item.params
    });
  }

  onPrimaryMenuItemClick(
    container: HTMLElement | null,
    wrapper: HTMLElement | null,
    item: NavigationBarItem,
    index: number
  ): void {
    if (!item.items) {
      if (item.url) {
        this.onMenuItemClick(item);
      }
      return;
    }

    if (!container || !wrapper) {
      return;
    }

    if (item.items.length == 1) {
      this.onMenuItemClick(item.items[0]);
      return;
    }

    if (this.currentOpenedMenu) {
      this.#closeSecondaryMenu(
        this.currentOpenedMenu.container,
        this.currentOpenedMenu.wrapper
      );
    }

    if (index === this.currentOpenedMenu?.index) {
      this.currentOpenedMenu = undefined;
      return;
    }

    this.currentOpenedMenu = {
      index,
      container,
      wrapper
    };

    this.#openSecondaryMenu(container, wrapper);
  }

  onSecondaryMenuItemClick(item: NavigationBarItem): void {
    this.onMenuItemClick(item);
  }

  onMouseLeave(): void {
    if (this.currentOpenedMenu) {
      this.#closeSecondaryMenu(
        this.currentOpenedMenu.container,
        this.currentOpenedMenu.wrapper
      );
      this.currentOpenedMenu = undefined;
    }
  }

  #openSecondaryMenu(container: HTMLElement, wrapper: HTMLElement): void {
    container.style.gridTemplateRows = `1fr`;
  }

  #closeSecondaryMenu(container: HTMLElement, wrapper: HTMLElement): void {
    wrapper.style.overflow = `hidden`;
    container.style.gridTemplateRows = `0fr`;
  }

  onCheckUpdates(): void {
    this.updateService.check();
  }

  onToogleNavigationBarMode(): void {
    this.toggleMode.emit();
  }

  isMenuItemProtected(item: NavigationBarItem): boolean {
    this.#protectedTabService.protectedRoutesRevision();

    if (this.product !== SoftwareProduct.PMS) {
      return false;
    }

    return this.#protectedTabService.isNavigationBarItemProtected(item);
  }

  shouldShowPrimaryMenuLock(item: NavigationBarItem): boolean {
    const childCount: number = item.items?.length ?? 0;

    if (childCount > 1) {
      return false;
    }

    return this.isMenuItemProtected(item);
  }
}
